/**
 * PrintableReport - Secure print utility for CHIRINO STEEL
 * 
 * Uses a hidden iframe instead of window.open/document.write for safer HTML printing.
 * The iframe is created, populated, printed, and then cleaned up automatically.
 * 
 * Usage:
 *   import { printHTML } from '../lib/printUtils';
 *   printHTML('<html>...</html>', { title: 'My Report' });
 */

/**
 * Detect iOS / iPadOS. iPadOS 13+ reporta como Mac, por eso revisamos
 * touch events. iOS Safari bloquea window.print() desde iframes, por lo
 * que necesitamos abrir el HTML en pestaña nueva.
 */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (ua.includes('Mac') && typeof document !== 'undefined' && 'ontouchend' in document) return true;
  return false;
}

/**
 * Dispara el diálogo de impresión a partir de un HTML standalone.
 * Estrategia adaptada por plataforma:
 *  - iOS/iPadOS: abre pestaña nueva (Safari bloquea print() en iframes).
 *  - Desktop: iframe oculto same-origin con auto-print.
 */
// Inyecta un auto-print en el HTML SOLO para rutas que abren nueva pestaña/ventana
// (iOS y fallback desktop). En el iframe oculto NO se usa: allí print() se llama
// directo, y duplicar el script provocaba doble diálogo de impresión (Carlos, Jun 2026).
function _injectAutoPrint(html) {
  if (!html) return html;
  const script = '<script>window.addEventListener("load",function(){setTimeout(function(){try{window.focus();window.print();}catch(e){}},400);});</scr' + 'ipt>';
  return html.includes('</body>') ? html.replace('</body>', script + '</body>') : html + script;
}

export function triggerPrint(html) {
  if (!html) return;

  if (isIOS()) {
    try {
      const blob = new Blob([_injectAutoPrint(html)], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        // Popup bloqueado: fallback con anchor click
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
    } catch (_e) { /* empty */ }
    return;
  }

  // Desktop: iframe oculto same-origin (Chrome, Comet, Brave, Edge, Firefox)
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-10000px;left:-10000px;width:1px;height:1px;border:none;';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.srcdoc = html;
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (_e) {
        try {
          const blob = new Blob([_injectAutoPrint(html)], { type: 'text/html;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const w = window.open(url, '_blank', 'width=950,height=920,scrollbars=yes');
          if (w) { try { w.focus(); } catch (_e2) { /* empty */ } }
          setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
        } catch (_e3) { /* empty */ }
      }
      setTimeout(() => { if (iframe.parentNode) document.body.removeChild(iframe); }, 60000);
    }, 250);
  };
  document.body.appendChild(iframe);
}

const CHIRINO_HEADER = `
  <div style="text-align:center;border-bottom:3px solid #1a365d;padding-bottom:10px;margin-bottom:15px;">
    <h1 style="font-size:16px;color:#1a365d;margin:0 0 5px 0;">CHIRINO STEEL ING. Y CONT. SPA</h1>
    <p style="font-size:10px;color:#4a5568;margin:0;">INGENIERIA - FABRICACION - MONTAJE</p>
  </div>
`;

const CHIRINO_FOOTER = (text) => `
  <div style="text-align:center;font-size:7px;color:#718096;margin-top:10px;border-top:1px solid #e2e8f0;padding-top:5px;">
    ${text || 'Generado por Sistema de Gestion CHIRINO STEEL'} | ${new Date().toLocaleString('es-CL')}
  </div>
`;

const BASE_STYLES = `
  @page { size: letter portrait; margin: 10mm; }
  body { font-family: Arial, sans-serif; font-size: 9px; margin: 0; padding: 10px; color: #1a202c; }
  table { width: 100%; border-collapse: collapse; font-size: 8px; }
  th { background: #1a365d; color: white; font-size: 7px; padding: 4px 3px; border: 1px solid #1a365d; text-transform: uppercase; }
  td { padding: 3px 4px; border: 1px solid #cbd5e0; }
  .header { text-align: center; border-bottom: 3px solid #1a365d; padding-bottom: 10px; margin-bottom: 15px; }
  .header h1 { font-size: 16px; color: #1a365d; margin: 0 0 5px 0; }
  .header p { font-size: 10px; color: #4a5568; margin: 0; }
  .summary { display: flex; justify-content: space-between; margin-bottom: 12px; gap: 6px; }
  .summary-box { flex: 1; text-align: center; padding: 6px; border: 1px solid #333; background: #f7fafc; }
  .summary-box.highlight { background: #e9d8fd; }
  .summary-box .label { font-size: 7px; color: #4a5568; text-transform: uppercase; }
  .summary-box .value { font-size: 10px; font-weight: bold; font-family: monospace; }
  .totals-row td { background: #1a365d !important; color: white !important; font-weight: bold; }
  .footer { text-align: center; font-size: 7px; color: #718096; margin-top: 10px; border-top: 1px solid #e2e8f0; padding-top: 5px; }
  .section-title { font-size: 12px; font-weight: bold; color: #1a365d; margin: 12px 0 6px 0; border-bottom: 1px solid #e2e8f0; padding-bottom: 3px; }
`;

/**
 * Print an HTML string using a hidden iframe (safer than document.write in a popup).
 * @param {string} htmlContent - Full HTML document or just the body content
 * @param {object} opts - Options
 * @param {string} opts.title - Document title (default: 'Reporte CHIRINO STEEL')
 * @param {boolean} opts.isFullHTML - If true, htmlContent is a complete HTML document. If false, wraps it.
 * @param {string} opts.extraStyles - Additional CSS to inject
 * @param {number} opts.delay - Delay before printing in ms (default: 300)
 */
export function printHTML(htmlContent, opts = {}) {
  const {
    title = 'Reporte CHIRINO STEEL',
    isFullHTML = false,
    extraStyles = '',
    delay = 300,
  } = opts;

  let fullHTML = htmlContent;
  if (!isFullHTML) {
    fullHTML = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
      <style>${BASE_STYLES}${extraStyles}</style></head><body>${htmlContent}</body></html>`;
  }

  // Create hidden iframe with srcdoc (avoids document.write XSS)
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-10000px;left:-10000px;width:0;height:0;border:none;';
  iframe.srcdoc = fullHTML;
  document.body.appendChild(iframe);

  // Wait for content to load, then print
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {

        // Fallback: open Blob URL in new window (avoids document.write)
        const blob = new Blob([fullHTML], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, '_blank', 'width=900,height=700');
        if (w) {
          w.onload = () => { w.focus(); w.print(); setTimeout(() => { w.close(); URL.revokeObjectURL(url); }, 500); };
        } else {
          URL.revokeObjectURL(url);
        }
      }
      // Cleanup iframe after print dialog closes
      setTimeout(() => {
        if (iframe.parentNode) document.body.removeChild(iframe);
      }, 1000);
    }, delay);
  };
}

/**
 * Build a standard CHIRINO STEEL report HTML document.
 * @param {object} opts
 * @param {string} opts.title - Report title (shown in header)
 * @param {string} opts.subtitle - Optional subtitle line
 * @param {Array} opts.summaryBoxes - Array of { label, value, color?, highlight? }
 * @param {string} opts.tableHTML - Raw HTML for the main table (<table>...</table>)
 * @param {string} opts.extraStyles - Additional CSS
 * @param {string} opts.footerText - Custom footer text
 * @returns {string} Full HTML document string
 */
export function buildReport({ title, subtitle, summaryBoxes, tableHTML, extraStyles = '', footerText, banner, fontScale }) {
  const summaryHTML = summaryBoxes?.length ? `
    <div class="summary">
      ${summaryBoxes.map(b => `
        <div class="summary-box${b.highlight ? ' highlight' : ''}">
          <div class="label">${b.label}</div>
          <div class="value"${b.color ? ` style="color:${b.color}"` : ''}>${b.value}</div>
        </div>
      `).join('')}
    </div>
  ` : '';

  const subtitleHTML = subtitle ? `<p>${subtitle}</p>` : '';

  // Iter 292.c (Carlos, feb-2026) — banner de estado (ej. "NO APROBADA")
  // para documentos que necesitan advertir su condición al ser impresos.
  const bannerHTML = banner?.text ? `
    <div style="text-align:center; margin: 8px 0 12px 0; padding: 10px 16px;
                border: 3px solid ${banner.color || '#c53030'};
                background: ${banner.bg || '#fff5f5'};
                color: ${banner.color || '#c53030'};
                font-size: ${18 * (fontScale || 1)}px; font-weight: 800; letter-spacing: 2px;
                text-transform: uppercase; border-radius: 6px;">
      ${banner.text}
    </div>
  ` : '';

  // Iter 292.d (Carlos, feb-2026) — fontScale escala todos los font-size
  // del reporte multiplicando por el factor recibido (ej: 1.15 = +15%).
  // Se aplica como override tras BASE_STYLES para respetar la cascada.
  const scaledStyles = fontScale && fontScale !== 1 ? `
    body { font-size: ${(9 * fontScale).toFixed(2)}px !important; }
    table { font-size: ${(8 * fontScale).toFixed(2)}px !important; }
    th { font-size: ${(7 * fontScale).toFixed(2)}px !important; }
    td { font-size: ${(8 * fontScale).toFixed(2)}px !important; }
    .header h1 { font-size: ${(16 * fontScale).toFixed(2)}px !important; }
    .header p, .header h2 { font-size: ${(11 * fontScale).toFixed(2)}px !important; }
    .summary-box .label { font-size: ${(7 * fontScale).toFixed(2)}px !important; }
    .summary-box .value { font-size: ${(10 * fontScale).toFixed(2)}px !important; }
    .footer { font-size: ${(7 * fontScale).toFixed(2)}px !important; }
    .section-title { font-size: ${(12 * fontScale).toFixed(2)}px !important; }
    body > p { font-size: ${(9 * fontScale).toFixed(2)}px !important; }
  ` : '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title} - CHIRINO STEEL</title>
    <style>${BASE_STYLES}${extraStyles}${scaledStyles}</style></head><body>
    ${CHIRINO_HEADER.replace('</h1>', `</h1><h2 style="font-size:13px;color:#2d3748;margin:0;">${title}</h2>`)}
    ${subtitleHTML ? `<p style="text-align:center;color:#4a5568;margin-top:-10px;">${subtitle}</p>` : ''}
    ${bannerHTML}
    ${summaryHTML}
    ${tableHTML || ''}
    ${CHIRINO_FOOTER(footerText)}
  </body></html>`;
}

/** Alias semántico de `exportToCSV` (genera XLSX real). */
export const exportToExcel = exportToCSV;

/** Format a number as Chilean pesos */
export const fmtCLP = (num) => (num || 0).toLocaleString('es-CL');

/**
 * Helper de alto nivel que combina todo: genera un reporte CHIRINO STEEL
 * imprimible y dispara `print()` desde un iframe oculto same-origin (evita
 * bloqueos de window.print en Comet/Brave/Safari y popup blockers).
 *
 * @param {object} opts
 * @param {string} opts.title - Título del reporte (ej: "Listado de OCs")
 * @param {string} [opts.subtitle] - Subtítulo opcional (ej: "Mes: Mayo 2026")
 * @param {Array<{label:string, value:string|number, color?:string, highlight?:boolean}>} [opts.summary]
 * @param {Array<{key:string, label:string, format?:Function, align?:'left'|'right'|'center', width?:string, color?:string, mono?:boolean}>} opts.columns
 * @param {Array<object>} opts.rows - Datos
 * @param {Array<{label:string, value:string|number, span?:number}>} [opts.totals] - Filas de totales al pie
 */
export function printTable({ title, subtitle, summary, columns, rows, totals, banner, fontScale }) {
  if (!columns?.length) return;
  const escapeHtml = (str) => String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Build summary boxes
  const summaryBoxes = summary?.map((s) => ({
    label: s.label,
    value: s.value,
    color: s.color,
    highlight: s.highlight,
  })) || [];

  // Build table HTML
  const colHeaders = columns.map((c) => {
    const align = c.align || 'left';
    const w = c.width ? `width:${c.width};` : '';
    const ta = align === 'right' ? 'text-align:right;' : align === 'center' ? 'text-align:center;' : '';
    return `<th style="${w}${ta}">${escapeHtml(c.label)}</th>`;
  }).join('');

  const rowsHTML = rows.map((r) => {
    const cells = columns.map((c) => {
      let val = r[c.key];
      if (c.format) {
        try { val = c.format(val, r); } catch (_e) { /* empty */ }
      }
      const isHTML = c.html === true;
      const align = c.align || 'left';
      const ta = align === 'right' ? 'text-align:right;' : align === 'center' ? 'text-align:center;' : '';
      const mono = c.mono ? 'font-family:monospace;' : '';
      const color = c.color ? `color:${c.color};` : '';
      const cellContent = isHTML ? (val ?? '') : escapeHtml(val ?? '');
      return `<td style="${ta}${mono}${color}">${cellContent}</td>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  const totalsHTML = totals?.length ? totals.map((t) => {
    const span = t.span || (columns.length - 1);
    return `<tr class="totals-row">
      <td colspan="${span}" style="text-align:right;">${escapeHtml(t.label)}</td>
      <td style="text-align:right;font-family:monospace;">${escapeHtml(t.value)}</td>
    </tr>`;
  }).join('') : '';

  const tableHTML = `
    <table>
      <thead><tr>${colHeaders}</tr></thead>
      <tbody>
        ${rowsHTML || `<tr><td colspan="${columns.length}" style="text-align:center;color:#999;padding:10px;">Sin datos</td></tr>`}
        ${totalsHTML}
      </tbody>
    </table>
  `;

  const html = buildReport({ title, subtitle, summaryBoxes, tableHTML, banner, fontScale });
  triggerPrint(html);
}

/**
 * Export data to a real XLSX file (SheetJS). Open natively in Excel,
 * Google Sheets, LibreOffice, Apple Numbers.
 *
 * Sigue llamándose `exportToCSV` por compatibilidad histórica con los 9
 * lugares que la usan. Acepta `delimiter` por compat pero el output es xlsx.
 *
 * @param {object} opts
 * @param {string} opts.filename - File name without extension (default: 'reporte')
 * @param {Array<{key:string, label:string, format?:Function}>} opts.columns - Column defs
 * @param {Array<object>} opts.rows - Row data
 * @param {Array<{label:string, value:string|number}>} [opts.summary] - Optional summary rows on top
 * @param {string} [opts.title] - Optional title row at the very top
 * @param {string} [opts.sheetName] - Excel sheet name (default: 'Reporte')
 */
export function exportToCSV({ filename = 'reporte', columns, rows, summary, title, sheetName = 'Reporte' }) {
  if (!columns?.length) return;

  // Lazy import: solo carga la lib cuando el usuario realmente exporta
  // (evita inflar el bundle inicial con ~700kb de SheetJS)
  import(/* webpackChunkName: "xlsx" */ 'xlsx').then((XLSXMod) => {
    const XLSX = XLSXMod.default || XLSXMod;
    const sheetData = [];
    const merges = [];
    let rowIdx = 0;

    // Title row (merged across all columns)
    if (title) {
      sheetData.push([title, ...Array(columns.length - 1).fill('')]);
      merges.push({ s: { r: rowIdx, c: 0 }, e: { r: rowIdx, c: columns.length - 1 } });
      rowIdx++;
      sheetData.push([]);
      rowIdx++;
    }

    // Summary section (label, value)
    if (summary?.length) {
      summary.forEach((s) => {
        const v = (typeof s.value === 'number') ? s.value : (s.value ?? '');
        sheetData.push([s.label, v, ...Array(Math.max(0, columns.length - 2)).fill('')]);
        rowIdx++;
      });
      sheetData.push([]);
      rowIdx++;
    }

    // Header row
    const headerRowIdx = rowIdx;
    sheetData.push(columns.map((c) => c.label));
    rowIdx++;

    // Data rows
    rows.forEach((row) => {
      const cells = columns.map((c) => {
        let val = row[c.key];
        if (c.format) {
          try { val = c.format(val, row); } catch (_e) { /* empty */ }
        }
        if (val === null || val === undefined) return '';
        // Detectar números para que Excel los reconozca como tales
        if (typeof val === 'number') return val;
        if (typeof val === 'string' && /^-?\d+(?:\.\d+)?$/.test(val)) return Number(val);
        return val;
      });
      sheetData.push(cells);
      rowIdx++;
    });

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    if (merges.length) ws['!merges'] = merges;

    // Auto-width: estimar ancho de columna por contenido (cap 50 chars)
    ws['!cols'] = columns.map((c, ci) => {
      let max = c.label?.length || 10;
      for (let r = 0; r < Math.min(rows.length, 1000); r++) {
        const val = sheetData[headerRowIdx + 1 + r]?.[ci];
        const len = val == null ? 0 : String(val).length;
        if (len > max) max = len;
      }
      return { wch: Math.min(50, Math.max(8, max + 2)) };
    });

    // Freeze header
    ws['!freeze'] = { xSplit: 0, ySplit: headerRowIdx + 1 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));

    const today = new Date().toISOString().slice(0, 10);
    const finalName = `${filename}_${today}.xlsx`;

    // En iOS/iPadOS: window.open(blobUrl) → Safari ofrece "Abrir en Numbers/
    // Excel" o "Guardar en Archivos". Si usamos <a download> directamente,
    // iOS abre el contenido inline y se ve como texto binario.
    // En desktop: XLSX.writeFile usa <a download> que dispara descarga directa.
    if (isIOS()) {
      const arr = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      const blob = new Blob([arr], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        // Popup blocked: fallback a anchor click
        const a = document.createElement('a');
        a.href = url;
        a.download = finalName;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
      return;
    }

    XLSX.writeFile(wb, finalName);
  }).catch((err) => {
     
    console.error('Error cargando xlsx, fallback a CSV', err);
    _exportToCSVFallback({ filename, columns, rows, summary, title });
  });
}

/** Fallback CSV (UTF-8 BOM) si la carga de xlsx falla por algún motivo. */
function _exportToCSVFallback({ filename, columns, rows, summary, title, delimiter = ';' }) {
  const escapeCell = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    if (s.includes(delimiter) || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [];
  if (title) { lines.push(escapeCell(title)); lines.push(''); }
  if (summary?.length) {
    summary.forEach((s) => lines.push([escapeCell(s.label), escapeCell(s.value)].join(delimiter)));
    lines.push('');
  }
  lines.push(columns.map((c) => escapeCell(c.label)).join(delimiter));
  rows.forEach((row) => {
    const cells = columns.map((c) => {
      let val = row[c.key];
      if (c.format) { try { val = c.format(val, row); } catch (_e) { /* empty */ } }
      return escapeCell(val);
    });
    lines.push(cells.join(delimiter));
  });
  const csv = '\ufeff' + lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { if (a.parentNode) document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}
